import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { DeleteOutlined } from '@ant-design/icons';
import { Avatar, Button, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

import RemoteAccessUsersV2Context from '@totem/components/services/remoteAccess/v2/users/RemoteAccessUsersV2Context';
import {
  QRUserRole,
  UserRoleStatus,
} from '@totem/components/services/remoteAccess/v2/users/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintAny } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const NEVER_LOGGED_IN = '0001-01-01T00:00:00Z';

const formatDate = (dateStr?: string): string => {
  if (!dateStr || dateStr === NEVER_LOGGED_IN) {
    return 'Never';
  }
  return new Date(dateStr).toLocaleDateString();
};

const STATUS_TAG_COLOR: Record<UserRoleStatus, string> = {
  ACTIVE: 'success',
  STALE: 'error',
  'NEVER LOGGED IN': 'warning',
};

const STATUS_TAG_LABEL: Record<UserRoleStatus, string> = {
  ACTIVE: 'Active',
  STALE: 'Stale',
  'NEVER LOGGED IN': 'Never Logged In',
};

type Props = {
  onRemoveUser: (row: QRUserRole) => void;
};

const RemoteAccessUsersV2Table = ({ onRemoveUser }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { rows, loading, selectedUserIds, setSelectedUserIds } = useContext(
    RemoteAccessUsersV2Context,
  );

  const isRemoteAccessAdmin = permissions.remoteAccessAdmin.hasPermissions(
    userProfile,
    securityGroupCheckConstraintAny,
  );

  const columns: ColumnsType<QRUserRole> = [
    {
      title: '',
      key: 'picture',
      width: 50,
      render: (_, record) => (
        <Avatar size="default" shape="circle" src={record.user.picture} />
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => (
        <Link
          to={`/dashboard/remoteAccess/auth0user/${record.user.email}?from=remoteAccessDashboard:-:Users`}
        >
          {record.user.email}
        </Link>
      ),
      sorter: (aRow, bRow) => aRow.user.email.localeCompare(bRow.user.email),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => record.user.name,
      sorter: (aRow, bRow) => aRow.user.name.localeCompare(bRow.user.name),
    },
    {
      title: 'Roles',
      key: 'roles',
      render: (_, record) => (
        <>
          {record.roles.map((role) => (
            <Tag key={role.id} style={{ marginBottom: 4, marginRight: 4 }}>
              <Link
                to={`/dashboard/remoteAccess/auth0role/${role.id}?from=remoteAccessDashboard:-:Users`}
              >
                {role.name}
              </Link>
            </Tag>
          ))}
        </>
      ),
      sorter: (aRow, bRow) => aRow.roles.length - bRow.roles.length,
    },
    {
      title: 'Login Status',
      key: 'loginStatus',
      render: (_, record) => (
        <Tag color={STATUS_TAG_COLOR[record.status]}>
          {STATUS_TAG_LABEL[record.status] ?? record.status}
        </Tag>
      ),
      sorter: (aRow, bRow) => aRow.status.localeCompare(bRow.status),
    },
    {
      title: 'Last Login',
      key: 'lastLogin',
      render: (_, record) => formatDate(record.user.last_login),
      sorter: (aRow, bRow) =>
        aRow.user.last_login.localeCompare(bRow.user.last_login),
    },
    {
      title: 'Created',
      key: 'createdAt',
      render: (_, record) => formatDate(record.user.created_at),
      sorter: (aRow, bRow) =>
        aRow.user.created_at.localeCompare(bRow.user.created_at),
    },
    ...(isRemoteAccessAdmin
      ? [
          {
            title: 'Actions',
            key: 'actions',
            width: 70,
            render: (_: unknown, record: QRUserRole) => (
              <Tooltip title="Remove from all roles">
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onRemoveUser(record)}
                />
              </Tooltip>
            ),
          } as ColumnsType<QRUserRole>[number],
        ]
      : []),
  ];

  return (
    <IBTable<QRUserRole>
      showSorterTooltip
      columns={columns}
      dataSource={rows}
      loading={loading}
      rowKey={(record) => record.user.user_id}
      pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      rowSelection={
        isRemoteAccessAdmin
          ? {
              selectedRowKeys: selectedUserIds,
              preserveSelectedRowKeys: true,
              onChange: (keys) => setSelectedUserIds(keys as string[]),
            }
          : undefined
      }
    />
  );
};

export default RemoteAccessUsersV2Table;
